import React, { useState, useCallback } from 'react';
import styled from 'styled-components/macro';
import { withRouter } from 'react-router-dom';
import scaleSize from 'utils/scale-size';
import { FocusableParent } from '../navigation/index';
import MenuItem from './MenuItem';
import config from '../../config';
import dispatchKeyboardEvent from 'utils/dispatch-keyboard-event';
import SpatialNavigationWrapper from '../navigation/SpatialNavigationWrapper';
import dashboardTagUrl from './dashboard-tag-url';
import customGA from '../../helpers/customGA';
import innerAnalyticsVolia from '../../helpers/innerAnalyticsVolia';

const SECTION_NAME = 'MAIN_MENU';

const MenuWrapper = styled(FocusableParent)`
  width: ${scaleSize(540)}px;
  height: ${scaleSize(1080)}px;
  background: ${({ theme, isPlayer }) => (isPlayer ? 'rgba(0,0,0,0.4)' : theme.menuBackground)};
  opacity: ${({ idle }) => (idle ? 0 : 1)};
  position: absolute;
  top: 0;
  left: 0;
  z-index: 100;
  transform: translate3d(${({ isFocused }) => (isFocused ? 0 : -scaleSize(440))}px, 0, 0);
  transition: ${config.withAnimation ? `transform ${config.mainTransitionDuration}` : 'none'};
  overflow: hidden;
`;

const Overlay = styled.div`
  width: ${scaleSize(1920)}px;
  height: ${scaleSize(1080)}px;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;
  background: ${({ theme }) => theme.overlayColor};
  opacity: ${({ isFocused }) => (isFocused ? 1 : 0)};
  transform: translate3d(${({ isFocused }) => (isFocused ? 0 : -scaleSize(1920))}px, 0, 0);
  transition: ${config.withAnimation ? `opacity ${config.mainTransitionDuration}` : 'none'};
`;

const MenuItemsWrapper = styled.div`
  width: ${scaleSize(540)}px;
  position: absolute;
  top: 50%;
  transform: translate3d(0, -50%, 0);

  span {
    opacity: ${({ isFocused }) => +isFocused};
  }
`;

const Menu = ({ menuItems, leaveForRight, isPlayer, idle = false, history, match }) => {
  const [isFocused, setIsFocused] = useState(false);

  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);

  const openMenu = useCallback(() => !isFocused && SpatialNavigationWrapper.focus(`@${SECTION_NAME}`), [isFocused]);
  const closeMenu = useCallback(() => dispatchKeyboardEvent({ keyCode: 39, key: 'ArrowRight', which: 39 }), []);

  const onEnter = useCallback(
    (item) => {
      const url = dashboardTagUrl[item.dashboardTag] || `/${item.dashboardTag.toLowerCase()}`;

      const isTheSamePath = match.url === url;
      const isNavigationInsidePlayer = match.url.includes('player') && url.includes('player');

      if (!isFocused || isTheSamePath || isNavigationInsidePlayer) return closeMenu();

      const itemDashboardTag = item.dashboardTag || 'UNTAGGED';

      innerAnalyticsVolia.postEvent({
        eventGroup: `dashboard: ${itemDashboardTag}; ${item.id}`,
        eventName: 'open dashboard',
      });

      customGA.postEvent('menu', item.title);

      history.push(`${url}`);
    },
    [match.url, isFocused, closeMenu, history],
  );

  return (
    <>
      <Overlay isFocused={isFocused} onClick={closeMenu} />
      {menuItems && (
        <MenuWrapper
          sectionName={SECTION_NAME}
          id={SECTION_NAME}
          config={{
            preventMouseNavigateIn: true,
            preventMouseNavigateWithin: true,
            leaveFor: {
              up: '',
              down: '',
              left: '',
              right: leaveForRight,
            },
            defaultElement: `#${MenuItem.ACTION_ITEM_ID}`,
            enterTo: 'default-element',
          }}
          isPlayer={isPlayer}
          idle={idle && isPlayer}
          isFocused={isFocused}
          onClick={openMenu}
        >
          <MenuItemsWrapper isFocused={isFocused}>
            {menuItems.map((item) => (
              <MenuItem
                isMenuFocused={isFocused}
                onFocus={onFocus}
                onBlur={onBlur}
                onEnter={onEnter}
                item={item}
                isActive={item.active}
                key={`menu-item-${item.id}`}
              />
            ))}
          </MenuItemsWrapper>
        </MenuWrapper>
      )}
    </>
  );
};

export default withRouter(Menu);
